import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Alert, CircularProgress, Button, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AmateurEditModal from './modals/AmateurEditModal';
import GiraAmaRegistration from './modals/GiraAmaRegistration';
import GiraAmaData from './data/GiraAmaData';
import GiraAmaIns from './data/GiraAmaIns';
import FichaDeposito from './generatePDF/fichaDeposito';
import { incrementEventInsForEtapa } from '../lib/appwrite';
import { API_BASE_URL } from '../config/api';


// Map API amateur record to form initialData for AmateurEditModal
const amateurRecordToInitialData = (record) => {
  if (!record) return { email: '' };
  const fnac = record.AmateurFNacimiento;
  const fnacimiento = fnac
    ? (typeof fnac === 'string' ? fnac : new Date(fnac).toISOString().split('T')[0])
    : '';
  return {
    email: record.AmateurMail || '',
    nombre: record.AmateurNombre || '',
    apellidos: record.AmateurApellidos || '',
    club: record.clubcodigo != null ? String(record.clubcodigo) : '',
    sexo: record.AmateurSexo || '',
    fnacimiento,
    curp: record.AmateurCURP || '',
    telefono: record.AmateurTelefono != null ? String(record.AmateurTelefono) : '',
    celular: record.AmateurCelular != null ? String(record.AmateurCelular) : '',
    ghin: record.AmateurGHIN || '',
    facebook: record.AmateurFacebook || '',
    instagram: record.AmateurInstagram || ''
  };
};

const Amateur = ({ user, amateurData, onUpdate, clubes = [] }) => {
  const [isAmateurEditOpen, setIsAmateurEditOpen] = React.useState(false);
  const [amateurEditMode, setAmateurEditMode] = React.useState('create'); // 'create' | 'edit'
  const [amateurEditInitialData, setAmateurEditInitialData] = React.useState({ email: user?.email });
  
  // State for amateur giras
  const [giras, setGiras] = useState([]);
  const [selectedGira, setSelectedGira] = useState(null);
  const [isLoadingGiras, setIsLoadingGiras] = useState(true);
  const [openGiraModal, setOpenGiraModal] = useState(false);
  const [isSavingInscription, setIsSavingInscription] = useState(false);
  const savingInscriptionRef = useRef(false); // bloqueo síncrono para evitar doble envío al hacer doble clic
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // State for inscripciones (registros) del amateur - mismo patrón que giras
  const [registros, setRegistros] = useState([]);
  const [isLoadingRegistros, setIsLoadingRegistros] = useState(false);
  const [generatingFicha, setGeneratingFicha] = useState(false);

  const getClipUrlForImporte = async (importe, referencia) => {
    const value = Number(importe) || 0;
    if (value <= 0) return null;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/amateur/clip-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: value, referencia })
      });
      if (!resp.ok) {
        console.error('Error al generar link Clip Amateur:', await resp.text());
        return null;
      }
      const data = await resp.json();
      return data?.url || null;
    } catch (e) {
      console.error('Error de red al generar link Clip Amateur:', e);
      return null;
    }
  };

  // Fetch the current Gira Amateur from the API
  useEffect(() => {
    let isMounted = true;

    const fetchGiras = async () => {
      setIsLoadingGiras(true);
      try {
        const girasData = await GiraAmaData.fetch();
        if (isMounted) {
          setGiras(girasData);
          if (Array.isArray(girasData) && girasData.length > 0) {
            setSelectedGira(girasData[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching amateur giras data:', error);
      } finally {
        if (isMounted) {
          setIsLoadingGiras(false);
        }
      }
    };

    fetchGiras();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch inscripciones (registros) del amateur cuando hay perfil amateur
  useEffect(() => {
    let isMounted = true;
    const amateurCodigo = amateurData?.[0]?.AmateurCodigo;

    const fetchRegistros = async () => {
      if (!amateurCodigo) {
        setRegistros([]);
        return;
      }
      setIsLoadingRegistros(true);
      try {
        const inscripciones = await GiraAmaIns.fetch(amateurCodigo);
        if (isMounted) {
          setRegistros(Array.isArray(inscripciones) ? inscripciones : []);
        }
      } catch (error) {
        console.error('Error fetching amateur inscripciones:', error);
        if (isMounted) setRegistros([]);
      } finally {
        if (isMounted) setIsLoadingRegistros(false);
      }
    };

    fetchRegistros();

    return () => {
      isMounted = false;
    };
  }, [amateurData]);

  const handleGiraInscription = (gira) => {
    setSelectedGira(gira);
    setOpenGiraModal(true);
  };

  const handleCloseGiraModal = () => {
    setOpenGiraModal(false);
    setSelectedGira(null);
  };

  const openAmateurCreateModal = () => {
    setAmateurEditMode('create');
    setAmateurEditInitialData({ email: user?.email });
    setIsAmateurEditOpen(true);
  };

  const handleRegisterAmateurFromGiraModal = () => {
    handleCloseGiraModal();
    openAmateurCreateModal();
  };

  const handleSaveGiraInscription = async (registrationData) => {
    if (savingInscriptionRef.current) return;
    savingInscriptionRef.current = true;
    setIsSavingInscription(true);

    try {
      const data = await GiraAmaIns.save(registrationData);

      if (registrationData.isEventual && Number(registrationData.etapa) > 0) {
        try {
          await incrementEventInsForEtapa(registrationData.giracodigo, registrationData.etapa);
        } catch (e) {
          console.error('No se pudo actualizar cupo de eventuales:', e);
        }
      }

      setSnackbar({
        open: true,
        message: data.message || 'Inscripción realizada exitosamente',
        severity: 'success'
      });
      handleCloseGiraModal();
      const girasData = await GiraAmaData.fetch();
      setGiras(girasData);
      // Refrescar registros para deshabilitar "Inscribirse" en esta gira
      const amateurCodigo = amateurData?.[0]?.AmateurCodigo;
      if (amateurCodigo) {
        const inscripciones = await GiraAmaIns.fetch(amateurCodigo);
        setRegistros(Array.isArray(inscripciones) ? inscripciones : []);
      }
    } catch (error) {
      console.error('Error saving amateur inscription:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al guardar la inscripción',
        severity: 'error'
      });
    } finally {
      savingInscriptionRef.current = false;
      setIsSavingInscription(false);
    }
  };

  return (
    <>
      {/* Alert for users without amateur profile */}
      {(!amateurData || amateurData.length === 0) && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: 7,
            cursor: 'pointer',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              p: 0,
              fontSize: '1.5rem',
            },
            '& .MuiAlert-message': {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              py: 0.25,
            },
            '&:hover': {
              backgroundColor: 'warning.light',
            }
          }}
          onClick={openAmateurCreateModal}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Registrar como Jugador Amateur
          </Typography>
                     <Typography variant="body2">
             Necesitas registrar tu perfil como jugador amateur para poder participar. Click para registrarte.
           </Typography>
        </Alert>
      )}

      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Gira Amateur
          </Typography>
          {amateurData && amateurData.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => {
                setAmateurEditMode('edit');
                setAmateurEditInitialData(amateurRecordToInitialData(amateurData[0]));
                setIsAmateurEditOpen(true);
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Editar jugador
            </Button>
          )}
        </Box>
        
        {isLoadingGiras ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Cargando giras amateur...
            </Typography>
          </Box>
        ) : !Array.isArray(giras) || giras.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2
          }}>
            <Typography color="text.secondary">
              No hay giras amateur vigentes
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {giras.map((gira, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  mb: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {gira.foto && (
                      <Box
                        component="img"
                        src={gira.foto}
                        alt={`Logo ${gira.nombre}`}
                        sx={{
                          width: 110,
                          height: 110,
                          objectFit: 'contain',
                          borderRadius: 3,
                          display: { xs: 'none', sm: 'block' }
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {gira.nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`${new Date(gira.inicio).toLocaleDateString()} - ${new Date(gira.fin).toLocaleDateString()}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    {(() => {
                      const insactual = gira.insactual || 0;
                      const insmaximo = gira.insmaximo;
                      const isFull = insmaximo && insactual >= insmaximo;
                      // Ya inscrito: existe registro para esta gira con etapa 0 (todas) o etapa actual (1 = eventual)
                      const hasRegistration = (registros || []).some(
                        (r) => Number(r.AmaInsGira) === Number(gira.gira) && (Number(r.AmaInsEtapa) === 0 || Number(r.AmaInsEtapa) === 1)
                      );
                      const cannotRegister = isFull || hasRegistration;

                      return (
                        <>
                          {isFull && (
                            <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>
                              Inscripciones llenas (Sin cupo disponible)
                            </Typography>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleGiraInscription(gira)}
                            disabled={cannotRegister}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              px: 3,
                              bgcolor: cannotRegister ? 'grey.400' : 'primary.main',
                              '&:hover': {
                                bgcolor: cannotRegister ? 'grey.400' : 'primary.dark'
                              }
                            }}
                          >
                            {isFull ? 'Sin cupo' : hasRegistration ? 'Inscrito' : 'Inscribirse'}
                          </Button>
                        </>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Registros Actuales (igual que Infantiles) */}
                {isLoadingRegistros ? (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Cargando registros...</Typography>
                  </Box>
                ) : (() => {
                  const currentRegistros = (registros || []).filter(
                    (r) => Number(r.AmaInsGira) === Number(gira.gira)
                  );
                  return currentRegistros.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Registro Actual
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                        {currentRegistros.map((reg, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 2,
                              alignItems: { xs: 'flex-start', sm: 'center' }
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 0.5, sm: 2 },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                flex: 1
                              }}
                            >
                              <Typography variant="body2">
                                {reg.AmaInsNombre || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Edad: {reg.AmaInsEdad != null ? reg.AmaInsEdad : '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Ref: {reg.AmaInsReferencia != null ? reg.AmaInsReferencia : '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {Number(reg.AmaInsEtapa) === 0
                                  ? 'Gira completa'
                                  : `Etapa: ${reg.AmaInsEtapa != null ? reg.AmaInsEtapa : '—'}`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                ml: { xs: 0, sm: 'auto' },
                                flexWrap: 'wrap',
                                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                maxWidth: '100%',
                                width: { xs: '100%', sm: 'auto' }
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={generatingFicha}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  minWidth: { xs: '120px', sm: '150px' },
                                  maxWidth: { xs: '140px', sm: '150px' },
                                  px: { xs: 1, sm: 2 }
                                }}
                                onClick={async () => {
                                  if (generatingFicha) return;
                                  setGeneratingFicha(true);
                                  try {
                                    const importeBase = Number(reg.AmaInsImporte) || 0;
                                    const refCount = Number(reg.ReferenciasUsadas) || 0;
                                    const descuentoReferencias = Math.min(importeBase, refCount * 2000);
                                    const pdfBlob = await FichaDeposito({
                                      jugador: reg.AmaInsNombre || '',
                                      gira: gira.nombre,
                                      jugadorRef: String(reg.AmaInsReferencia != null ? reg.AmaInsReferencia : ''),
                                      giraImporte: importeBase,
                                      wagrImporte: 0,
                                      cijImporte: 0,
                                      descuentoReferencias,
                                      banco: 'BANAMEX',
                                      cuenta: '39545',
                                      sucursal: '4417',
                                      clabe: '002542441700395457',
                                      beneficiario: 'Asociación de Golf del Sur A.C.',
                                      clipUrl: await getClipUrlForImporte(
                                        Math.max(0, importeBase - descuentoReferencias),
                                        reg.AmaInsReferencia
                                      ),
                                    });
                                    const url = window.URL.createObjectURL(pdfBlob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `ficha_deposito_${(reg.AmaInsNombre || 'amateur').replace(/\s+/g, '_')}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                    if (reg.AmaInsNumero != null) {
                                      await GiraAmaIns.markImpreso(reg.AmaInsNumero);
                                    }
                                  } catch (error) {
                                    console.error('Error generating PDF:', error);
                                    setSnackbar({ open: true, message: 'Error al generar el PDF', severity: 'error' });
                                  } finally {
                                    setGeneratingFicha(false);
                                  }
                                }}
                              >
                                {generatingFicha ? (
                                  <CircularProgress size={20} sx={{ color: 'inherit' }} />
                                ) : (
                                  <>
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Ficha de Deposito</Box>
                                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Ficha</Box>
                                  </>
                                )}
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : null;
                })()}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Amateur Edit Modal */}
      <AmateurEditModal
        open={isAmateurEditOpen}
        onClose={() => setIsAmateurEditOpen(false)}
        initialData={amateurEditInitialData}
        onUpdate={onUpdate}
        isCreateMode={amateurEditMode === 'create'}
        clubes={clubes}
      />

      {/* Gira Amateur Registration Modal */}
      <GiraAmaRegistration
        open={openGiraModal}
        onClose={handleCloseGiraModal}
        selectedGira={selectedGira}
        amateurData={amateurData}
        clubes={clubes}
        user={user}
        onSave={handleSaveGiraInscription}
        isSaving={isSavingInscription}
        onRegisterAmateurProfile={handleRegisterAmateurFromGiraModal}
        isReferenciaEspecialEligible={(() => {
          if (!selectedGira) return false;
          const currentGira = Number(selectedGira.gira);
          if (!Number.isFinite(currentGira)) return false;
          // Elegible si NO jugó la gira anterior (currentGira - 1), independientemente de otras giras.
          const prevGira = currentGira - 1;
          const hasPrev = Array.isArray(registros) && registros.some(
            (r) => Number(r.AmaInsGira) === prevGira
          );
          return !hasPrev;
        })()}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default Amateur;
